import React from 'react';
import RichTextEditor from './RichTextEditor';

function SportsEditor({ pageContent, setPageContent, uploading, uploadingField, addTableRow, deleteTableRow, updateTableRow, handleRowImageUpload }) {
  
  const handleSportsImageUpload = async (file) => {
    if (!file) return;
    try {
      const { uploadImage } = await import('../../firebase/firebase-config');
      const url = await uploadImage(file, `sports/banner_${Date.now()}`);
      const newImages = [...(pageContent.sliderImages || []), { url }];
      setPageContent({ ...pageContent, sliderImages: newImages });
    } catch (error) {
      alert('Error uploading image: ' + error.message);
    }
  };

  const deleteSportsImage = async (index) => {
    const image = (pageContent.sliderImages || [])[index];
    if (image?.url && window.confirm('Delete this banner image permanently?')) {
      try {
        const { deleteImageFromStorage } = await import('../../firebase/firebase-config');
        await deleteImageFromStorage(image.url);
        const newImages = (pageContent.sliderImages || []).filter((_, i) => i !== index);
        setPageContent({ ...pageContent, sliderImages: newImages });
      } catch (error) {
        alert('Error deleting image: ' + error.message);
      }
    } else {
      const newImages = (pageContent.sliderImages || []).filter((_, i) => i !== index);
      setPageContent({ ...pageContent, sliderImages: newImages });
    }
  };

  return (
    <div className="page-editor">
      {/* Banner Images Section */}
      <div className="editor-card">
        <h3>📸 Sports Page Banner Images</h3>
        <div className="form-group">
          <label>Banner Images (Will appear as slider on top of Sports page)</label>
          <div className="banner-images-list">
            {(pageContent.sliderImages || []).map((img, idx) => (
              <div key={idx} className="banner-image-item">
                <img src={img.url} alt={`Banner ${idx + 1}`} className="banner-preview" />
                <button className="remove-btn-small" onClick={() => deleteSportsImage(idx)}>🗑️ Remove</button>
              </div>
            ))}
          </div>
          <label className="upload-label">
            {uploading && uploadingField === 'sportsBanner' ? '⏳ Uploading...' : '+ Upload Banner Image'}
            <input 
              type="file" 
              accept="image/*" 
              style={{ display: 'none' }} 
              onChange={async (e) => {
                if (e.target.files[0]) {
                  await handleSportsImageUpload(e.target.files[0]);
                  e.target.value = '';
                }
              }} 
            />
          </label>
          <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
            Recommended size: 1920×600 pixels. Images will display as a slider at the top of the sports page.
          </small>
        </div>
      </div>

      {/* Sports Overview Section with Rich Text */}
      <div className="editor-card">
        <h3>⚽ Sports Program Overview</h3>
        <div className="form-group">
          <label>Sports Program Description (Rich Text)</label>
          <RichTextEditor 
            value={pageContent.sportsOverview || ''}
            onChange={(html) => setPageContent({...pageContent, sportsOverview: html})}
            placeholder="Write about the sports program at your school..."
            imageFolder="sports-overview-images"
          />
        </div>
      </div>

      {/* Sports Offered Section */}
      <div className="editor-card">
        <h3>🏀 Sports Offered</h3>
        <div className="form-group">
          <label>Show Sports Offered Section?</label>
          <select 
            value={pageContent.showSportsOffered || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showSportsOffered: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {(pageContent.sports || []).map((sport, idx) => (
          <div key={sport.id} className="sport-editor-item">
            <button 
              className="remove-btn" 
              onClick={() => deleteTableRow('sports', sport.id)}
              style={{ position: 'absolute', top: 10, right: 10 }}
            >
              🗑️ Delete Sport
            </button>
            <div className="form-group">
              <label>Icon (emoji)</label>
              <input 
                placeholder="e.g., ⚽, 🏀, 🏏, 🏸" 
                value={sport.icon || '⚽'} 
                onChange={(e) => updateTableRow('sports', sport.id, 'icon', e.target.value)} 
                className="form-input" 
              />
            </div>
            <div className="form-group">
              <label>Sport Name</label>
              <input 
                placeholder="e.g., Football, Basketball, Cricket" 
                value={sport.name || ''} 
                onChange={(e) => updateTableRow('sports', sport.id, 'name', e.target.value)} 
                className="form-input" 
              />
            </div>
            <div className="form-group">
              <label>Description (Rich Text)</label>
              <RichTextEditor 
                value={sport.description || ''}
                onChange={(html) => updateTableRow('sports', sport.id, 'description', html)}
                placeholder="Describe the sport, facilities, coaching, etc."
                imageFolder="sports-description-images"
              />
            </div>
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('sports', { icon: '⚽', name: 'New Sport', description: '' })}>
          + Add Sport
        </button>
      </div>

      {/* Sports Achievements Section */}
      <div className="editor-card">
        <h3>🏆 Sports Achievements</h3>
        <div className="form-group">
          <label>Show Achievements Section?</label>
          <select 
            value={pageContent.showAchievements || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showAchievements: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {(pageContent.sportsAchievements || []).map((ach, idx) => (
          <div key={ach.id} className="achievement-editor-item">
            <button 
              className="remove-btn" 
              onClick={() => deleteTableRow('sportsAchievements', ach.id)}
              style={{ position: 'absolute', top: 10, right: 10 }}
            >
              🗑️ Delete Achievement
            </button>
            <div className="form-group">
              <label>Achievement Title</label>
              <input 
                placeholder="e.g., State Champions, National Winners" 
                value={ach.title || ''} 
                onChange={(e) => updateTableRow('sportsAchievements', ach.id, 'title', e.target.value)} 
                className="form-input" 
              />
            </div>
            <div className="form-group">
              <label>Year</label>
              <input 
                placeholder="e.g., 2024" 
                value={ach.year || ''} 
                onChange={(e) => updateTableRow('sportsAchievements', ach.id, 'year', e.target.value)} 
                style={{ width: 120 }} 
                className="form-input" 
              />
            </div>
            <div className="form-group">
              <label>Description (Rich Text)</label>
              <RichTextEditor 
                value={ach.description || ''}
                onChange={(html) => updateTableRow('sportsAchievements', ach.id, 'description', html)}
                placeholder="Describe the achievement..."
                imageFolder="sports-achievement-images"
              />
            </div>
            <div className="form-group">
              <label>Achievement Image (optional)</label>
              {ach.image && (
                <div className="image-preview">
                  <img src={ach.image} className="preview-img-sm" />
                </div>
              )}
              <label className="upload-label">
                {uploading && uploadingField === `sportsAchievements_${ach.id}_image` ? '⏳ Uploading...' : '📷 Upload Image'}
                <input 
                  type="file" 
                  accept="image/*" 
                  style={{ display: 'none' }} 
                  onChange={(e) => { 
                    if (e.target.files[0]) handleRowImageUpload('sportsAchievements', ach.id, 'image', e.target.files[0]); 
                  }} 
                />
              </label>
              {ach.image && (
                <button className="remove-btn-small" onClick={() => {
                  if (window.confirm('Delete this image?')) {
                    updateTableRow('sportsAchievements', ach.id, 'image', '');
                  }
                }}>🗑️ Remove Image</button>
              )}
            </div>
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('sportsAchievements', { title: '', description: '', year: '', image: '' })}>
          + Add Achievement
        </button>
      </div>

      {/* Sports Videos Section */}
      <div className="editor-card">
        <h3>🎬 Sports Videos (YouTube)</h3>
        <div className="form-group">
          <label>Show Videos Section?</label>
          <select 
            value={pageContent.showVideos || 'yes'} 
            onChange={(e) => setPageContent({...pageContent, showVideos: e.target.value})} 
            className="form-input"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {(pageContent.sportsVideos || []).map((video, idx) => (
          <div key={video.id} className="video-editor-item">
            <button 
              className="remove-btn" 
              onClick={() => deleteTableRow('sportsVideos', video.id)}
              style={{ position: 'absolute', top: 10, right: 10 }}
            >
              🗑️ Delete Video
            </button>
            <div className="form-group">
              <label>YouTube URL</label>
              <input 
                placeholder="e.g., https://www.youtube.com/watch?v=xxxxx" 
                value={video.url || ''} 
                onChange={(e) => updateTableRow('sportsVideos', video.id, 'url', e.target.value)} 
                className="form-input" 
              />
            </div>
            <div className="form-group">
              <label>Video Title</label>
              <input 
                placeholder="e.g., Sports Day Highlights 2024" 
                value={video.title || ''} 
                onChange={(e) => updateTableRow('sportsVideos', video.id, 'title', e.target.value)} 
                className="form-input" 
              />
            </div>
          </div>
        ))}
        <button className="add-btn" onClick={() => addTableRow('sportsVideos', { url: '', title: '' })}>
          + Add Video
        </button>
        <small style={{ display: 'block', marginTop: '8px', color: '#64748b' }}>
          💡 Tip: Paste any YouTube video URL (e.g., https://www.youtube.com/watch?v=xxxxx)
        </small>
      </div>

      <style>{`
        .page-editor {
          max-width: 1200px;
          margin: 0 auto;
        }

        .editor-card {
          background: #fff;
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .editor-card h3 {
          margin: 0 0 20px;
          color: #1a3a6b;
          font-size: 22px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #334155;
          font-size: 14px;
        }

        .form-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 14px;
        }

        .add-btn {
          background: #10b981;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: background 0.3s;
        }

        .add-btn:hover {
          background: #059669;
        }

        .remove-btn {
          background: #ef4444;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.3s;
        }

        .remove-btn-small {
          background: #ef4444;
          color: white;
          border: none;
          padding: 4px 10px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          margin-top: 8px;
        }

        .upload-label {
          background: #3b82f6;
          color: white;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          display: inline-block;
          transition: background 0.3s;
        }

        .upload-label:hover {
          background: #2563eb;
        }

        .preview-img-sm {
          width: 80px;
          height: 60px;
          object-fit: contain;
          border-radius: 8px;
          background: #f5f5f5;
          margin-bottom: 8px;
        }

        .sport-editor-item,
        .achievement-editor-item,
        .video-editor-item {
          border: 1px solid #e2e8f0;
          padding: 20px;
          margin-bottom: 20px;
          border-radius: 12px;
          position: relative;
          background: #fafbfc;
        }

        .banner-images-list {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-bottom: 15px;
        }

        .banner-image-item {
          position: relative;
          display: inline-block;
        }

        .banner-preview {
          width: 120px;
          height: 70px;
          object-fit: contain;
          border-radius: 8px;
          border: 2px solid #e2e8f0;
          background: #000;
        }

        .image-preview {
          margin-bottom: 10px;
        }

        @media (max-width: 768px) {
          .editor-card {
            padding: 16px;
          }
          .editor-card h3 {
            font-size: 18px;
          }
          .banner-preview {
            width: 100px;
            height: 60px;
          }
        }
      `}</style>
    </div>
  );
}

export default SportsEditor;